
import {
  getLabelmapSegmentationCachedStats,
  getLabelVolumeFromSeriesId,
  getOrthoNormal,
  getPreViewVolumeFromSeriesId,
  getVolumeFromSeriesId,
  getVolumeMinMax,
  normalize
} from "@features/analysis/rendering/Panels/Renderers/CornerstoneManager";

import {
  axesLineRenderer,
  BufferPool,
  BufferPoolItem,
  fitActorToCanvas,
  setActorsCenter,
  updateResetCamera,
  VRContext
} from "@features/analysis/rendering/Panels/Renderers/vtkManagers";

import { scheduleRender } from "@features/analysis/rendering/Panels/renderState";
import { makeVolumeColorPoints, volumeColorType } from "config/colorUtils";
import { int16ToUint8 } from "./utilitiesManager";
import { runWorkerTask } from "@features/analysis/rendering/Panels/workerPool";
import { configStore } from "config/configStore";
import { CalciumAnalysisResult } from "@models/Parameter";

import vtkColorTransferFunction from "@kitware/vtk.js/Rendering/Core/ColorTransferFunction";
import vtkPiecewiseFunction from "@kitware/vtk.js/Common/DataModel/PiecewiseFunction";
import vtkVolumeMapper from "@kitware/vtk.js/Rendering/Core/VolumeMapper";
import vtkDataArray from "@kitware/vtk.js/Common/Core/DataArray";
import vtkImageData from "@kitware/vtk.js/Common/DataModel/ImageData";
import vtkVolume from "@kitware/vtk.js/Rendering/Core/Volume";
import vtkVolumeProperty from "@kitware/vtk.js/Rendering/Core/VolumeProperty";
import { BlendMode } from "@kitware/vtk.js/Rendering/Core/VolumeMapper/Constants";
import vtkMapper from "@kitware/vtk.js/Rendering/Core/Mapper";
import { RenderingQualityKeyType } from "config/settingsConfig";
import { Vector3 } from "@kitware/vtk.js/types";
import { AnalysisType, getPickingVolumeType, getSegShapeAnalysisType, getSegVolumeType } from "config/analysisConfig";
import vtkCamera from "@kitware/vtk.js/Rendering/Core/Camera";
import vtkPlane from "@kitware/vtk.js/Common/DataModel/Plane";
import { mat3, mat4, vec3, vec4 } from 'gl-matrix';
import { getDilateVolumeIndices, volumeKeys, volumeNames } from 'config/render3DConfig';
import { logVrLoadPerf, startVrLoadPerf } from "./vrLoadPerf";
import { LightingOption } from "config/config";

// export const globalHalf = { value: true };

type MIPValueRange = {
  brightMin: number;
  brightMax: number;
  darkMin: number;
  darkMax: number;
};

type UpdateMIPOptions = {
  ww: number;
  wl: number;
  hu: number[];
  negativeMode: boolean;
  range?: MIPValueRange;
  useOriginalGeometry?: boolean; // <- 여기로 이동 추천
};

const MIP_TRANSFER_MIN_WIDTH_HU = 1;

function normalizeMIPWindowRange(lower: number, upper: number): [number, number] {
  let safeLower = Number.isFinite(lower) ? lower : -2048;
  let safeUpper = Number.isFinite(upper) ? upper : 4096;

  if (safeUpper < safeLower) {
    [safeLower, safeUpper] = [safeUpper, safeLower];
  }

  if (safeUpper - safeLower < MIP_TRANSFER_MIN_WIDTH_HU) {
    const center = (safeLower + safeUpper) * 0.5;
    safeLower = center - MIP_TRANSFER_MIN_WIDTH_HU * 0.5;
    safeUpper = center + MIP_TRANSFER_MIN_WIDTH_HU * 0.5;
  }

  return [safeLower, safeUpper];
}

function resolveMIPTransferRange(params: {
  ww?: number;
  wl?: number;
  huMin: number;
  huMax: number;
}): [number, number] {
  const ww = Number(params.ww);
  const wl = Number(params.wl);
  const huMin = Number.isFinite(params.huMin) ? params.huMin : -2048;
  const huMax = Number.isFinite(params.huMax) ? params.huMax : 4096;

  if (!Number.isFinite(ww) || !Number.isFinite(wl) || ww <= 1) {
    return normalizeMIPWindowRange(huMin, huMax);
  }

  return normalizeMIPWindowRange(
    wl - 0.5 - (ww - 1) / 2,
    wl - 0.5 + (ww - 1) / 2,
  );
}

function addMIPOpacityPoints(
  opacityTF: vtkPiecewiseFunction,
  scalarMin: number,
  scalarMax: number,
) {
  const min = Number.isFinite(scalarMin) ? scalarMin : -2048;
  const max = Number.isFinite(scalarMax) ? scalarMax : 4096;

  opacityTF.removeAllPoints();
  opacityTF.addPoint(min, 0.0);
  opacityTF.addPoint(min + 1, 0.0);
  opacityTF.addPoint(min + 2, 1.0);
  opacityTF.addPoint(max, 1.0);
  opacityTF.sortAndUpdateRange();
  opacityTF.modified();
}

function applyMIPTransferFunction(params: {
  colorTF: vtkColorTransferFunction;
  opacityTF: vtkPiecewiseFunction;
  lower: number;
  upper: number;
  scalarMin: number;
  scalarMax: number;
  negativeMode?: boolean;
}) {
  const { colorTF, opacityTF, negativeMode } = params;
  const [lower, upper] = normalizeMIPWindowRange(params.lower, params.upper);
  const scalarMin = Number.isFinite(params.scalarMin) ? params.scalarMin : -2048;
  const scalarMax = Number.isFinite(params.scalarMax) ? params.scalarMax : 4096;
  const lowColor = negativeMode ? 1 : 0;
  const highColor = negativeMode ? 0 : 1;

  colorTF.removeAllPoints();
  colorTF.addRGBPoint(scalarMin, lowColor, lowColor, lowColor);
  colorTF.addRGBPoint(lower, lowColor, lowColor, lowColor);
  colorTF.addRGBPoint(upper, highColor, highColor, highColor);
  colorTF.addRGBPoint(scalarMax, highColor, highColor, highColor);
  colorTF.sortAndUpdateRange();
  colorTF.modified();

  addMIPOpacityPoints(opacityTF, scalarMin, scalarMax);
}

export function adjustSampleQuality(params:{
  mapper:vtkVolumeMapper,
  spacing:[number,number,number],
  dimensions:[number,number,number],
  quality:RenderingQualityKeyType,
}){
  const {mapper,dimensions,quality,spacing} = params;
  if(!mapper) return;

  const [sx, sy, sz] = spacing;
  const [nx, ny, nz] = dimensions;

   const diagMm = Math.sqrt(
    (nx * sx) * (nx * sx) +
    (ny * sy) * (ny * sy) +
    (nz * sz) * (nz * sz)
  );

  const minSpacing = Math.min(sx, sy, sz);

  let factor: number;
  let minCap: number;
  let maxCap: number;

  switch (quality) {
    case 'high':
      factor = 0.5;
      minCap = 512;
      maxCap = 2048;
      break;

    case 'medium':
      factor = 0.75;
      minCap = 384;
      maxCap = 1024;
      break;

    case 'low':
    default:
      factor = 1.0;
      minCap = 256;
      maxCap = 512;
      break;
  }

  const sampleDistance = Math.max(factor * minSpacing, (diagMm * 1.001) / maxCap);
  const desiredSamples = diagMm / sampleDistance;
  const maximumSamplesPerRay = Math.ceil(
    Math.min(maxCap, Math.max(minCap, desiredSamples)),
  );

  mapper.setAutoAdjustSampleDistances(false);
  mapper.setSampleDistance(sampleDistance);
  mapper.setMaximumSamplesPerRay(maximumSamplesPerRay);
}

function makeVolume(
  context: React.MutableRefObject<VRContext | null>,
  seriesId:string,
  name:volumeKeys,
  spacing: Vector3,
  dimensions: Vector3,
  output: Uint8Array | Int16Array,
  renderingQuality:RenderingQualityKeyType,
  useOriginalGeometry:boolean,
  analysisType: AnalysisType,
  optionsMIP?:UpdateMIPOptions,
){
  if (!context.current) return;
  const finishPerf = startVrLoadPerf("VRUtilities.makeVolume", {
    analysisType,
    seriesId,
    renderingQuality,
    useOriginalGeometry,
    volumeName: name,
  });
  try {
  const { renderer, actorIds, cellPicker } = context.current;
  const volume = actorIds[name] as vtkVolume ?? vtkVolume.newInstance();
  const mapper = getVolumeMapper({
    context,
    seriesId,
    name,
    volumeBuffer:output,
    half: renderingQuality!=='high',
    useOriginalGeometry,
    analysisType,
    renderingQuality,
  }) as vtkVolumeMapper;


  const renderingMode = useOriginalGeometry
    ? 'high'
    : renderingQuality;

  const volumeProperty = volume.getProperty()?? vtkVolumeProperty.newInstance();
  setVolumeLighting(volumeProperty);

  const colorTF = volumeProperty.getRGBTransferFunction(0);
  const opacityTF = volumeProperty.getScalarOpacity(0);
  colorTF.removeAllPoints();
  opacityTF.removeAllPoints();
  if(name === volumeNames.MIP && optionsMIP){
    const [ segHuMin, segHuMax ] = optionsMIP.hu;
    const [lower, upper] = resolveMIPTransferRange({
      ww: optionsMIP.ww,
      wl: optionsMIP.wl,
      huMin: segHuMin,
      huMax: segHuMax,
    });

    applyMIPTransferFunction({
      colorTF,
      opacityTF,
      lower,
      upper,
      scalarMin: segHuMin,
      scalarMax: segHuMax,
      negativeMode: optionsMIP.negativeMode,
    });
    volumeProperty.setShade(false);
  }else  if(name === volumeNames.calcium){
    //별도로 색상 조절
    colorTF.removeAllPoints();
    opacityTF.removeAllPoints();

    colorTF.addRGBPoint(0, 0, 0, 0);      // 배경
    colorTF.addRGBPoint(255, 1, 1, 1);    // 칼슘 (흰색)

    opacityTF.addPoint(0, 0.0);
    opacityTF.addPoint(255, 1);
  }else if(name === volumeNames.heartCut){
    let [rawMin, rawMax] = getVolumeMinMax(seriesId,renderingQuality!=='high');
      getColorTransferFunction(
      colorTF,
      opacityTF,
      true,
      { min: rawMin, max: rawMax },
      configStore.get().vrType,
    );
  }
  colorTF.sortAndUpdateRange();
  opacityTF.sortAndUpdateRange();

  colorTF.modified();
  opacityTF.modified();

  adjustSampleQuality({
    mapper,
    spacing,
    dimensions,
    quality:renderingMode,
  })
  volume.setMapper(mapper);
  const exists = !!renderer.getVolumes().find(v => v === volume);
  if(!exists){
    flipVolume(volume);
    renderer.addVolume(volume);
  }
  cellPicker?.addPickList(volume);
  context.current.actorIds[name] = volume;
  } finally {
    finishPerf();
  }
}

export async function runDilateDataVolume(
  context: React.MutableRefObject<VRContext | null>,
  seriesId: string | undefined,
  name: volumeKeys,
  init: boolean,
) {
  if (!context.current || !seriesId) return;
  if (!getDilateVolumeIndices(name)) return;

  let { segHuMin, segHuMax, actorIds } = context.current;
  const { visibleMIP, generalSetting } = configStore.get();
  const volume = actorIds[name] as vtkVolume;
  const volumeMapper = (volume as vtkVolume | undefined)?.getMapper();
  if (!volumeMapper) {
    return;
  }
  const paintData = volumeMapper.getInputData();
  const pointData = paintData.getPointData();
  const scalars = pointData.getScalars();
  const scalarData = scalars.getData() as Int16Array | Uint8Array;
  const dimensions = paintData.getDimensions();

  const half = generalSetting.renderingQuality !== 'high';
  const oriVolume = getVolumeFromSeriesId(seriesId, half)!;

  if (oriVolume.additionalDetails) {
    segHuMin = oriVolume.additionalDetails['HUMin'] as number;
    segHuMax = oriVolume.additionalDetails['HUMax'] as number;
  }

  const { outputData } = await runWorkerTask({
    type: 'dilateVolume',
    scalarData,
    dimensions,
    options: {
      isMIPMode: visibleMIP,
      segHuMin,
      segHuMax,
    },
  });
  if (!outputData) return;
  scalars.setData(outputData);
  scalars.modified();
  paintData.modified();
  volumeMapper.modified();
  volume?.modified();
  context.current.actorIds[name] = volume;
  configStore.setData((prev) => ({
    volumeDilation: {
      ...prev.volumeDilation,
      target: init
        ? [name]
        : [
            ...(prev.volumeDilation.target ?? []).filter((v) => v !== name),
            name,
          ],
    },
  }));
}

export async function runSegmentationVolume(
  context: React.MutableRefObject<VRContext | null>,
  buffers: BufferPoolItem,
  seriesId: string,
  analysisType: AnalysisType,
  options: any = {},
  setBufferPool?: React.Dispatch<React.SetStateAction<BufferPool>>,
){
  const perfMeta = {
    analysisType,
    heartCut: options.heartCut,
    renderingQuality: options.renderingQuality,
    seriesId,
    useOriginalGeometry: options.useOriginalGeometry,
    visibleKeyCount: Array.isArray(options.visibleKeys)
      ? options.visibleKeys.length
      : undefined,
    visibleMIP: options.visibleMIP,
  };
  const finishPerf = startVrLoadPerf(
    "VRUtilities.runSegmentationVolume",
    perfMeta,
  );
  try {
  if (!context.current) return;
  const { renderer, actorIds, cellPicker } = context.current;
  let { segHuMin, segHuMax, sharedContext } = context.current;

  const segmentationVolumeMapper = buffers['segmentationVolumeMapper'];
  if (!segmentationVolumeMapper) return;
  if (segmentationVolumeMapper.loaded && !segmentationVolumeMapper.data) {
    segmentationVolumeMapper.loaded = false;
    console.log('이거저거 누르는 행위로 인해 안맹들어짐, 다시 시도');
  }

  const half = options.renderingQuality!=='high';
  const oriVolume = getVolumeFromSeriesId(seriesId,half)!;
  const segVolume = getLabelVolumeFromSeriesId(seriesId,half)!;
  if (!segVolume) return;

  let {spacing,dimensions} = oriVolume;

  let oriData =
    oriVolume.voxelManager?.getCompleteScalarDataArray?.() as Int16Array | Uint8Array;

  let labelData =
    segVolume.voxelManager?.getCompleteScalarDataArray?.() as Uint8Array;

  const quality = options.useOriginalGeometry
    ? 'high'
    : options.renderingQuality;

  const paramData = buffers?.parameter.data as CalciumAnalysisResult;
  const p1 = paramData?.SavedInfo?.Coronary?.initialPoints1;
  const p2 = paramData?.SavedInfo?.Coronary?.initialPoints2;

  let selected: [number, number, number] | undefined;
  if (p1 && p2) {
    const cond = p1[0] < p2[0] && p1[1] > p2[1];
    selected = cond ? [...p1] as [number, number, number] : [...p2] as [number, number, number];
  } else if (p1) {
    selected = [...p1] as [number, number, number];
  } else if (p2) {
    selected = [...p2] as [number, number, number];
  } else {
    selected = undefined;
  }

  if(options.useOriginalGeometry){
    const {oriHUData, oriLabelData} = sharedContext;
    if(oriLabelData && oriHUData){
      const huScalars = oriHUData.getPointData().getScalars();
      const labelScalars = oriLabelData.getPointData().getScalars();
      if(!huScalars || !labelScalars) {
        console.log('oriLabelData undefined');
        return
      };
      oriData = huScalars.getData() as Uint8Array;
      labelData = labelScalars.getData() as Uint8Array;
      dimensions = oriLabelData.getDimensions();
      spacing = oriLabelData.getSpacing();
     //console.log('Low 인데 Original size');
   }
  }
  if(quality === 'low' && selected){
    const fpHalf = [
      selected[0] * 0.5,
      selected[1] * 0.5,
      selected[2] * 0.5,
    ] as [number, number, number];
    selected = fpHalf;
  }

  const cachedStats = getLabelmapSegmentationCachedStats(seriesId,half);

  const run = (type: string, extra: any) => runWorkerTask({ type, ...extra });

  if (oriVolume.additionalDetails) {
    segHuMin = oriVolume.additionalDetails['HUMin'] as number;
    segHuMax = oriVolume.additionalDetails['HUMax'] as number;
  }
  const isSegmentationShape = getSegShapeAnalysisType(analysisType);


  const coronaryKeys =
    isSegmentationShape
      ? ['coronary', 'aorta'] //coronaryWall 안쓰는거래...
      : [];

  const calciumKeys =
    (analysisType === 'plaque' && !actorIds[volumeNames.calcium])
      ? ['calcium']
      : [];

  //칼슘덩어리를 별도로 만들어서 사용하므로 seg 오버레이용에서는 굳이...
  const visibleKeys = options.visibleKeys
    .filter((key: string) => key !== 'calcium');

  const segGroups = {
    calcium: getSegmentationBits(cachedStats, calciumKeys),
    //coronary: getSegmentationBits(cachedStats, ['coronary', 'coronaryWall']),
    chamber: getSegmentationBits(cachedStats, analysisType ==='chamber' ? ['la', 'lv', 'ra', 'rv'] : []),
    epicardial: getSegmentationBits(
      cachedStats,
      [
          'la',
          'lv',
          'ra',
          'rv',
          'laWall',
          'lvWall',
          'raWall',
          'rvWall',
          'coronary',
          'coronaryWall',
          'aorta',
          'aorticValveCalcium',
        ],
        'excludeTarget',
        'epicardial',
      ),
    segVisible: getSegmentationBits(cachedStats, visibleKeys),
    cutVisible: getSegmentationBits(cachedStats, coronaryKeys),
  };

  const ModeMIP = configStore.get().renderingModeMIP;
  const MIPMode = {
    Ventricle:['lvWall', 'rvWall'],
    AortaCoronary: [],
    AortaCoronaryWall: ['laWall', 'lvWall', 'raWall', 'rvWall'],
    All: ['la', 'lv', 'ra', 'rv', 'laWall', 'lvWall', 'raWall', 'rvWall'],
  }[ModeMIP ?? 'All'];

  const MIPGroups = options.visibleMIP
    ? {
        bright: getSegmentationBits(cachedStats, [...coronaryKeys, 'calcium']),
        dark: getSegmentationBits(cachedStats, MIPMode),
      }
    : undefined;

  const finishCreateVrPerf = startVrLoadPerf(
    "VRUtilities.runSegmentationVolume.createVRWorker",
    perfMeta,
  );
  let createVrOutput: any;
  try {
    createVrOutput = await run('createVR', {
        scalarData: oriData,
        labelData,
        segGroups,
        MIPGroups,
        dimensions,
        hu: [segHuMin, segHuMax],
        enabled: {
          chamber: options.chamberEnabled ?? false,
          MIP: options.chamberEnabled
            ? false
            : (options.visibleMIP ?? false),
          heartCut: options.chamberEnabled
            ? false
            : (options.heartCut ?? false),
        },
        focalPoint: selected,
      });
  } finally {
    const workerPerf = createVrOutput?.perf;
    const outputBytes =
      workerPerf?.outputBytes ??
      ((createVrOutput?.ouputSegData?.byteLength ?? 0) +
        (createVrOutput?.ouputOverlayData?.byteLength ?? 0) +
        (createVrOutput?.outputChamberData?.byteLength ?? 0) +
        (createVrOutput?.outputHeartCutData?.byteLength ?? 0) +
        (createVrOutput?.outputMIPData?.byteLength ?? 0) +
        (createVrOutput?.outputCalciumData?.byteLength ?? 0));
    const createVrElapsedMs = finishCreateVrPerf({
      outputBytes,
      ...workerPerf,
    });
    if (workerPerf) {
      logVrLoadPerf(
        "VRUtilities.runSegmentationVolume.createVRWorker.worker.allocate",
        workerPerf.allocateMs ?? 0,
        { ...perfMeta, ...workerPerf },
      );
      logVrLoadPerf(
        "VRUtilities.runSegmentationVolume.createVRWorker.worker.lookupBuild",
        workerPerf.lookupBuildMs ?? 0,
        { ...perfMeta, ...workerPerf },
      );
      logVrLoadPerf(
        "VRUtilities.runSegmentationVolume.createVRWorker.worker.loop",
        workerPerf.loopMs ?? 0,
        { ...perfMeta, ...workerPerf },
      );
      logVrLoadPerf(
        "VRUtilities.runSegmentationVolume.createVRWorker.worker.total",
        workerPerf.workerTotalMs ?? 0,
        { ...perfMeta, ...workerPerf },
      );
      const roundTripOverheadMs = Math.max(
        createVrElapsedMs - (workerPerf.workerTotalMs ?? 0),
        0,
      );
      logVrLoadPerf(
        "VRUtilities.runSegmentationVolume.createVRWorker.roundTrip",
        roundTripOverheadMs,
        { ...perfMeta, ...workerPerf, roundTripOverheadMs },
      );
    }
  }
  const {
    ouputSegData,
    ouputOverlayData,
    outputChamberData,
    outputHeartCutData,
    outputMIPData,
    range,
    outputCalciumData
  } = createVrOutput;


  if (!segmentationVolumeMapper.loaded) {
    const finishMapperPerf = startVrLoadPerf(
      "VRUtilities.runSegmentationVolume.segmentationMapperCreate",
      perfMeta,
    );
    try {
      segmentationVolumeMapper.loaded = true;
      segmentationVolumeMapper.data = getVolumeMapper({
        context,
        seriesId,
        name: volumeNames.base,
        volumeBuffer:ouputSegData,
        half,
        analysisType,
        renderingQuality: options.renderingQuality,
      });
      if (setBufferPool) {
        setBufferPool((prev) => ({
          ...prev,
          [seriesId]: { ...prev[seriesId], segmentationVolumeMapper },
        }));
      }
    } finally {
      finishMapperPerf({ reusedMapper: false });
    }
  } else {
    logVrLoadPerf(
      "VRUtilities.runSegmentationVolume.segmentationMapperReuse",
      0,
      { ...perfMeta, reusedMapper: true },
    );
  }
  updateBaseVolumeMapper(
    context,
    buffers,
    seriesId,
    analysisType,
    options.chamberEnabled? outputChamberData: ouputOverlayData,
    {
      renderingQuality:options.renderingQuality,
      useOriginalGeometry:options.useOriginalGeometry
    },
  );

  if(options.heartCut){
    makeVolume(
      context,
      seriesId,
      'volumeHeartCut',
      spacing,
      dimensions,
      outputHeartCutData,
      options.renderingQuality,
      options.useOriginalGeometry,
      analysisType,
    );
  }
  if(!!segGroups.calcium?.size){
    console.log('칼슘덩어리 맹든다.')
    makeVolume(
      context,
      seriesId,
      'volumeCalcium',
      spacing,
      dimensions,
      outputCalciumData,
      options.renderingQuality,
      options.useOriginalGeometry,
      analysisType,
    );
  }
  if(options.visibleMIP){
    makeVolume(
      context,
      seriesId,
      'volumeMIP',
      spacing,
      dimensions,
      outputMIPData,
      options.renderingQuality,
      options.useOriginalGeometry,
      analysisType,
      {
        ww: options.ww,
        wl: options.wl,
        hu:[segHuMin, segHuMax],
        negativeMode: options.negativeMode,
        range,
      },
    )
  }
  const volumes = renderer.getVolumes();
  for (let i = 0; i < volumes.length; i++) {
    volumes[i].setVisibility(false);
  }
  } finally {
    finishPerf();
  }
}

export function initCameraForAnalysis(
  context: React.MutableRefObject<VRContext | null>,
  analysisType: AnalysisType,
  seriesId: string,
  pool?: BufferPoolItem,
) {
  if (!context.current) return;
  initVRCameraForAnalysis(
    context,
    analysisType,
    seriesId,
    pool,
  );
  context.current.axes = {
    x: [1, 0, 0],
    y: [0, 1, 0],
    z: [0, 0, 1],
  };
}


function initVRCameraForAnalysis(
  context: React.MutableRefObject<VRContext | null>,
  analysisType: AnalysisType,
  seriesId: string,
  pool?: BufferPoolItem,
) {
  if (!context.current) return;
  const { renderer, viewportId, actorIds, canvas } = context.current;
  const camera = renderer.getActiveCamera();
  const focalPoint = camera.getFocalPoint();
  const distance = camera.getDistance();
  camera.setViewUp(0, 0, -1);
  camera.setPosition(focalPoint[0], focalPoint[1] + distance, focalPoint[2]);

  const targetVolume = actorIds[volumeNames.base] as vtkVolume;
  if(!targetVolume) return;

  const exists = renderer.getVolumes().some(v => v === targetVolume);

  if(!exists){
    renderer.addVolume(targetVolume);
  }


  let bound = updateResetCamera(context,analysisType,pool);
  let bounds = bound?? targetVolume.getBounds();

  // renderer.resetCamera(bounds);
  // fitActorToCanvas(
  //   renderer,
  //   bounds,
  //   canvas,
  // );
  const factor = 0.5;
    // analysisType === 'tavi' && !actorIds[nodeNames.singleCenterLine]
    //   ? 0.25
    //   : 0.5;
  const center: [number, number, number] = [
    bounds[0] + (bounds[1] - bounds[0]) * factor,
    bounds[2] + (bounds[3] - bounds[2]) * factor,
    bounds[4] + (bounds[5] - bounds[4]) * factor,
  ];

  context.current.focus = [-center[0], center[1], center[2]];
  context.current.savedTranslation = [center[0], center[1], center[2]];

  // if (analysisType === 'tavi' && !actorIds[nodeNames.singleCenterLine]){
  //   const oldFocal = camera.getFocalPoint();
  //   const oldPos = camera.getPosition();

  //   const offset = [
  //     oldPos[0] - oldFocal[0],
  //     oldPos[1] - oldFocal[1],
  //     oldPos[2] - oldFocal[2],
  //   ];

  //   camera.setFocalPoint(...center);
  //   camera.setPosition(
  //     center[0] + offset[0],
  //     center[1] + offset[1],
  //     center[2] + offset[2],
  //   );
  // }


  renderer.updateLightsGeometryToFollowCamera();
  //chamber, ejectionFraction 굳이 포커스 이미지 중심으로 안보내도댐.
  if(!['chamber', 'ejectionFraction'].includes(analysisType)){
    requestAnimationFrame(() => {
      scheduleRender('Axial', {
        type: 'focus',
        world: [-center[0], center[1], center[2]],
        request: true,
      });
    });
    axesLineRenderer(context);
  }
}

export function updateEFVolumeMapper(
  context: React.MutableRefObject<VRContext | null>,
  buffers: BufferPoolItem,
  analysisType: AnalysisType,
  seriesId: string,
  efId: string,
  options: any = {},
) {
  const efData = buffers?.EF?.data as Record<
    string,
    {
      percent: number;
      data: Uint8Array;
      chamberInfo: {
        normal: [number, number, number];
        right: [number, number, number];
        up: [number, number, number];
        center: [number, number, number];
      };
    }
  >;
  console.log({
    efId,
    seriesId,
    efData,
    target: efData[efId],
  });

  if (efData[efId] === undefined) return;
  const { chamberInfo, data } = efData[efId];
  updateBaseVolumeMapper(context, buffers, seriesId, 'ejectionFraction', data, options);
  let { center, normal, right, up } = chamberInfo;
  const newCenter = center.map((v) => v * 0.5) as typeof center;
  const preViewVolume = getPreViewVolumeFromSeriesId(seriesId)!;
  const { imageData, dimensions } = preViewVolume;
  const focalPoint = imageData?.indexToWorld([
    -newCenter[0],
    -newCenter[1],
    newCenter[2],
  ]) as [number, number, number];
  const axes = [
    [0, 0, 0] as [number, number, number],
    normalize(up.map((v) => -v) as typeof normal),
    normal,
  ];
  axes[0] = getOrthoNormal(axes[1], axes[2]);
  initCameraForAnalysis(
    context,
    analysisType,
    seriesId,
  );

  updateCutter(
    context,
    0,
    {
      centers: [focalPoint],
      viewNormal: axes[2],
      normal: axes[1],
    },
    true,
  );
  return {
    centers: [focalPoint],
    viewNormal: axes[2],
    normal: axes[1],
  };
}


function setVolumeLighting(
  volumeProperty: vtkVolumeProperty,
  lightingOption?: LightingOption,
) {
  const lightingOptions = lightingOption?? configStore.get().lightingOptions.VR;
  volumeProperty.setShade(lightingOptions.enabled);
  if (!lightingOptions.enabled) return;
  volumeProperty.setAmbient(lightingOptions.ambient);
  volumeProperty.setDiffuse(0.7);
  volumeProperty.setSpecular(0.5);
  volumeProperty.setSpecularPower(20.0);
}

export function updateBaseVolumeMapper(
  context: React.MutableRefObject<VRContext | null>,
  buffers: BufferPoolItem,
  seriesId: string,
  analysisType: AnalysisType,
  output?: Uint8Array, // mpr이면 undefined임
  options: any = {},
) {
  const finishPerf = startVrLoadPerf("VRUtilities.updateBaseVolumeMapper", {
    analysisType,
    outputBytes: output?.byteLength,
    renderingQuality: options.renderingQuality,
    seriesId,
    useOriginalGeometry: options.useOriginalGeometry,
    volumeName: volumeNames.base,
  });
  try {
  if (!context.current) return;
  if (!buffers) return;

  const { renderer, actorIds, rendererId, canvas, segHuMax, segHuMin } =
    context.current;
  const half =
    ['MPR', 'ejectionFraction'].includes(analysisType) ||
    options.renderingQuality !== 'high';
  const { imageData } = getVolumeFromSeriesId(seriesId, half)!;

  let [rawMin, rawMax] = getVolumeMinMax(seriesId, half);
  const isSegVolume =
    getSegVolumeType(analysisType) || analysisType === 'ejectionFraction';
  if (!isSegVolume || (rawMin === 0 && rawMax === 0)) {
    rawMax = segHuMax;
    rawMin = segHuMin;
  }

  const volume =
    (actorIds[volumeNames.base] as vtkVolume) ?? vtkVolume.newInstance();
  let volumeMapper = volume?.getMapper() as vtkVolumeMapper | undefined;
  const volumeProperty = volume.getProperty();

  if (isSegVolume) {
    if (output) {
      volumeMapper = getVolumeMapper({
        context,
        seriesId,
        volumeBuffer: output,
        name: volumeNames.base,
        half,
        useOriginalGeometry: options.useOriginalGeometry,
        analysisType,
        renderingQuality: options.renderingQuality,
      });
    } else {
      //여기 뭘까... 일단 보류
      if (['segmentation', 'plaque'].includes(analysisType)) {
        volumeMapper = buffers.segmentationVolumeMapper.data as vtkVolumeMapper;
      }
    }
    setVolumeLighting(volumeProperty);
  } else {
    volumeMapper = getVolumeMapper({
      context,
      seriesId,
      name: volumeNames.base,
      half,
      analysisType,
      renderingQuality: options.renderingQuality,
    })!;
  }
  if (!volumeMapper) {
    return;
  }
  const input = volumeMapper.getInputData() as vtkImageData;
  const dimensions = input.getDimensions();
  const spacing = input.getSpacing();

  //useOriginalGeometry는 low일때 원본값으로 다시 만들때
  //'MPR', 'ejectionFraction' high 여도 절반사이즈 만들지만 VR Sampleing은 high로
  const quality = options.useOriginalGeometry
    ? 'high'
    : options.renderingQuality;
  adjustSampleQuality({
    mapper: volumeMapper,
    spacing,
    dimensions,
    quality,
  });

  const colorTF = volumeProperty.getRGBTransferFunction(0);
  const opacityTF = volumeProperty.getScalarOpacity(0);
  getColorTransferFunction(
    colorTF,
    opacityTF,
    isSegVolume,
    { min: rawMin, max: rawMax },
    analysisType === 'MPR' ? 'none' : configStore.get().vrType,
  );

  volumeMapper.modified();
  volume.setMapper(volumeMapper!);
  flipVolume(volume);

  const exists = !!renderer.getVolumes().find((v) => v === volume);
  if (!exists) {
    renderer.addVolume(volume);
    renderer.resetCamera();
    const bounds = volume.getBounds();
    renderer.updateLightsGeometryToFollowCamera();
    fitActorToCanvas(renderer, bounds, canvas);
  }

  if (imageData) {
    const centerIndex = dimensions.map((d) => d / 2) as Vector3;
    const world = imageData.indexToWorld(centerIndex);
    context.current.savedTranslation = [-world[0], world[1], world[2]];
    if (analysisType !== 'ejectionFraction') {
      context.current.focus = [world[0], world[1], world[2]];
    }
  }
  volume.modified();
  actorIds[volumeNames.base] = volume;

  setPickingForVolume(context, analysisType);
  setActorsCenter(context);
  requestAnimationFrame(() =>
    scheduleRender(rendererId!, { type: 'updateRenderWindow' }),
  );
  } finally {
    finishPerf();
  }
}

export function updateVolumeColor(
  context: React.MutableRefObject<VRContext | null>,
  seriesId: string,
  type: volumeColorType,
  options: any = {},
) {
  if (!context.current) return;
  const { renderer, rendererId, segHuMax, segHuMin, actorIds } = context.current;
  const half = configStore.get().generalSetting.renderingQuality!=='high';
  const [min, max] = getVolumeMinMax(seriesId, half);
  const huMin = min === 0 && max === 0 ? segHuMin : min;
  const huMax = min === 0 && max === 0 ? segHuMax : max;
  if(type === 'MIP'){
    const { ww, wl, negativeMode } = options;
    const [lower, upper] = resolveMIPTransferRange({
      ww,
      wl,
      huMin,
      huMax,
    });

    const volumeMIP = actorIds[volumeNames.MIP] as vtkVolume | undefined;
    if (!volumeMIP) return;

    const volumeProperty = volumeMIP.getProperty();
    const colorTF = volumeProperty.getRGBTransferFunction(0);
    const opacityTF = volumeProperty.getScalarOpacity(0);
    applyMIPTransferFunction({
      colorTF,
      opacityTF,
      lower,
      upper,
      scalarMin: huMin,
      scalarMax: huMax,
      negativeMode,
    });
    volumeProperty.setShade(false);
    volumeProperty.modified();
    volumeMIP.setVisibility(true);
    volumeMIP.modified();
  }else{
    for (let i = 0; i < renderer.getVolumes().length; i++) {
      const volume = renderer.getVolumes()[i];
      if(actorIds[volumeNames.MIP] === volume || actorIds[volumeNames.calcium] === volume) {
        continue;
      }
      const colorTF = volume.getProperty().getRGBTransferFunction(0);
      const opacityTF = volume.getProperty().getScalarOpacity(0);

      getColorTransferFunction(
        colorTF,
        opacityTF,
        true,
        { min: huMin, max: huMax },
        type,
      );
      volume.getProperty().modified();
      volume.modified();
      context.current.actorIds[i] = volume;
    }
  }

  scheduleRender(rendererId!, { type: 'updateRenderWindow' });
}

export function getSegmentationBits(
  segCachedStats: any,
  keys: string[],
  mode: 'targetOnly' | 'excludeTarget' = 'targetOnly',
  targetKey?: string,
): Set<number> | undefined {
  if(keys.length === 0) {
    console.log('없음')
    return;
  };
  const arrangeBitMask = segCachedStats['arrangeBitMask'] as Set<number>;
  if (mode === 'targetOnly') {
    return new Set<number>(
      Object.entries(arrangeBitMask)
        .filter(([k]) =>
          keys.some((key) =>
            (segCachedStats[key] as Set<number> | undefined)?.has(Number(k)),
          ),
        )
        .map(([_, v]) => v as number),
    );
  } else if (mode === 'excludeTarget') {
    if (!targetKey) return new Set<number>();
    const othersUnion = new Set<number>(
      keys.flatMap((key) =>
        Array.from((segCachedStats[key] as Set<number>) ?? []),
      ),
    );
    const targetSet = segCachedStats[targetKey] as Set<number>;

    const unique = targetSet
      ? [...targetSet].filter((v) => !othersUnion.has(v))
      : [];

    return new Set<number>(
      Object.entries(arrangeBitMask)
        .filter(([key, _]) => unique?.includes(Number(key)))
        .map(([_, value]) => value),
    );
  }
}

function flipVolume(volume: vtkVolume) {
  //const bounds = vol.getBounds();
  //const cx = (bounds[0] + bounds[1]) / 2;
  //vol.setPosition(cx * 2, 0, 0);
  volume?.setScale(-1, 1, 1);
}

function getVolumeMapper({
  context,
  seriesId,
  name,
  half = false,
  volumeBuffer,
  useOriginalGeometry,
  analysisType,
  renderingQuality,
}: {
  context: React.MutableRefObject<VRContext | null>;
  seriesId: string;
  name: volumeKeys;
  half?: boolean;
  volumeBuffer?: Uint8Array | Int16Array;
  useOriginalGeometry?:boolean;
  analysisType?: AnalysisType;
  renderingQuality?: string;
}) {
  const finishPerf = startVrLoadPerf("VRUtilities.getVolumeMapper", {
    analysisType,
    renderingQuality,
    seriesId,
    useOriginalGeometry,
    volumeName: name,
  });
  try {
  if (!context.current) return;
  const { sharedContext } = context.current;
  const { oriHUData } = sharedContext;

  const cachedVolume = getVolumeFromSeriesId(seriesId,half);
  if(!cachedVolume) return;

  let { voxelManager, dimensions, origin, spacing, direction } = cachedVolume;
   if(useOriginalGeometry && oriHUData){
    dimensions = oriHUData.getDimensions()
    spacing = oriHUData.getSpacing();
    origin = oriHUData.getOrigin();
  }
  if(useOriginalGeometry){
    console.log('Low인데 Original',volumeBuffer?.length,dimensions)
  }

  if (!volumeBuffer) {
    //기본 mpr 볼륨이 필요할때 쓰는 넘
    const { segHuMin, segHuMax } = context.current;
    const scalar = voxelManager!.getCompleteScalarDataArray!() as Int16Array;
    volumeBuffer = new Uint8Array(scalar.length);
    for (let i = 0; i < scalar.length; i++) {
      volumeBuffer[i] = int16ToUint8(scalar[i], segHuMin, segHuMax);
    }
  }
  const volume = context.current.actorIds[name ?? volumeNames.base] as vtkVolume;
  let volumeMapper = (volume as vtkVolume | undefined)?.getMapper();
  if (!volumeMapper) {
    volumeMapper = vtkVolumeMapper.newInstance();
  }
  let paintData = volumeMapper.getInputData();
  if (!paintData) {
    paintData = vtkImageData.newInstance();
  }

  const pointData = paintData.getPointData();
  const scalars = pointData.getScalars();
  if (scalars) {
    scalars.setData(volumeBuffer);
  } else {
    const newScalars = vtkDataArray.newInstance({
      name: 'Scalars',
      numberOfComponents: 1,
      values: volumeBuffer,
    });
    pointData.setScalars(newScalars);
  }

  if(name === volumeNames.MIP)
    volumeMapper.setBlendMode(BlendMode.MAXIMUM_INTENSITY_BLEND);
  paintData.setDirection(direction);
  paintData.setDimensions(dimensions);
  paintData.setSpacing(spacing);
  paintData.setOrigin(origin);
  volumeMapper.setInputData(paintData);
  volumeMapper.modified?.();
  return volumeMapper;
  } finally {
    finishPerf({ outputBytes: volumeBuffer?.byteLength });
  }
}

export function getClampMIPRange(
  seriesId: string,
  half: boolean,
  ww:number,
  wl:number,
) {
  const originalVolume = getVolumeFromSeriesId(seriesId,half);
  if(!originalVolume )return false;
  const { additionalDetails } = originalVolume;
  let segHuMax = 4096;
  let segHuMin = -2048;
  if (additionalDetails) {
    segHuMax = additionalDetails['HUMax'] as number;
    segHuMin = additionalDetails['HUMin'] as number;
  }

  const [lower, upper] = resolveMIPTransferRange({
    ww,
    wl,
    huMin: segHuMin,
    huMax: segHuMax,
  });

  return Math.round(lower) !== Math.round(upper);
}

function getColorTransferFunction(
  colorTF: vtkColorTransferFunction,
  opacityTF: vtkPiecewiseFunction,
  segMode: boolean,
  hu: { min: number; max: number },
  type: volumeColorType,
  negativeMode?:boolean,
) {
  //VR Type 따라~
  const { min: segHuMin, max: segHuMax } = hu;
  const hexToNormalizedRGBA = (hex: string): [number, number, number] => {
    // #RRGGBB or #RRGGBBAA 지원
    const value = hex.replace('#', '');
    const hasAlpha = value.length === 8;
    const r = parseInt(value.substring(0, 2), 16);
    const g = parseInt(value.substring(2, 4), 16);
    const b = parseInt(value.substring(4, 6), 16);
    const a = hasAlpha ? parseInt(value.substring(6, 8), 16) : 255;
    return [r / 255, g / 255, b / 255];
  };

  colorTF.removeAllPoints();

  if(type  === 'MIP'){
    applyMIPTransferFunction({
      colorTF,
      opacityTF,
      lower: segHuMin,
      upper: segHuMax,
      scalarMin: Math.min(-2048, segHuMin),
      scalarMax: Math.max(4096, segHuMax),
      negativeMode,
    });
  }else{
    const opacityPoints = segMode
    ? [
        { hu: -2048, a: 0.0 },
        { hu: segHuMin, a: 0.0 },
        { hu: -800, a: 0.3 },
        { hu: -200, a: 0.5 },
        { hu: 0, a: 0.3 },
        { hu: 60, a: 0.1 },
        { hu: 150, a: 0.3 },
        { hu: 300, a: 0.6 },
        { hu: 600, a: 0.5 },
        { hu: 800, a: 0.4 },
        { hu: 1200, a: 0.7 },
        { hu: segHuMax, a: 1.0 },
        { hu: 4096, a: 0.0 },
      ]
    : [
        { hu: -2048, a: 0.0 },
        { hu: -1024, a: 0.0 },
        { hu: 128, a: 0.0 },
        { hu: 129, a: 0.0982143 },
        { hu: 173, a: 0.669643 },
        { hu: 255, a: 0.857143 },
        { hu: 584, a: 0.866071 },
        { hu: 4096, a: 1.0 },
      ];

    let rawPoints = makeVolumeColorPoints(type, hu);
    rawPoints.forEach(([hu, hex]) => {
      const [r, g, b] = hexToNormalizedRGBA(hex);
      colorTF.addRGBPoint(int16ToUint8(hu, segHuMin, segHuMax), r, g, b);
    });
    colorTF.addRGBPoint(255, 1, 1, 1);

    opacityTF.removeAllPoints();
    opacityTF.addPoint(0, 0.0);
    const nodes = opacityPoints
      .map(({ hu, a }) => ({ x: int16ToUint8(hu, segHuMin, segHuMax), y: a }))
      .sort((a, b) => a.x - b.x)
      .filter((p, i, arr) => i === 0 || p.x !== arr[i - 1].x);
    nodes.forEach(({ x, y }) => opacityTF.addPoint(x, y));
    opacityTF.addPoint(255, 1);
    opacityTF.sortAndUpdateRange();
    opacityTF.modified();
  }
  colorTF.sortAndUpdateRange();
  colorTF.modified();
}

export function setPickingForVolume(
  context: React.MutableRefObject<VRContext | null>,
  analysisType: AnalysisType,
) {
  if (!context.current) return;
  const {cellPicker, actorIds} = context.current;
  if (!cellPicker) return;
  if (!getPickingVolumeType(analysisType)) return;

  cellPicker.setTolerance(0.01);
  cellPicker.initializePickList();
  cellPicker.setPickFromList(true);

  for (const [key, actor] of Object.entries(actorIds)) {
    if ((actor as any).isA?.('vtkVolume')) {
      if(key === volumeNames.MIP) continue;
      cellPicker.addPickList(actor);
    }
  }
}

export function updateCutter(
  context: React.MutableRefObject<VRContext | null>,
  newIndex: number,
  chamberViewProps: any,
  rotate: boolean,
) {
  if (!context.current) return;
  const {
    renderer,
    actorIds,
    rendererId
   } = context.current;
  const focalPoint = chamberViewProps.centers[newIndex] as [
    number,
    number,
    number,
  ];
  if (!focalPoint) return;
  const viewPlaneNormal = chamberViewProps.viewNormal.map((v: number) =>
    Number(v.toFixed(3)),
  );
  const viewUp = chamberViewProps.normal.map((v: number) =>
    Number(v.toFixed(3)),
  );

  if (renderer.getVolumes().length === 0) return;

  const volume = actorIds[volumeNames.base] as vtkVolume; //renderer.getVolumes()[0] as vtkVolume;
  const volumeMapper = volume.getMapper() as vtkVolumeMapper;
  volume.setVisibility(true);
  const newfocalPoint = [
    -focalPoint[0],
    focalPoint[1],
    focalPoint[2],
  ] as Vector3;
  const newViewPlaneNormal = [
    -viewPlaneNormal[0],
    viewPlaneNormal[1],
    viewPlaneNormal[2],
  ] as Vector3;
  if (!volumeMapper.getClippingPlanes().length) {
    const plane = vtkPlane.newInstance();
    plane.setOrigin(newfocalPoint);
    plane.setNormal(newViewPlaneNormal);
    volumeMapper.addClippingPlane(plane);
  } else {
    const oldPlane = volumeMapper.getClippingPlanes()[0];
    oldPlane.setOrigin(newfocalPoint);
    oldPlane.setNormal(newViewPlaneNormal);
    oldPlane.modified();
  }
  volumeMapper.modified();
  volume.modified();
  if (rotate) {
    const camera: vtkCamera = renderer.getActiveCamera();
    const curPos = camera.getPosition() as [number, number, number];
    const curFocal = camera.getFocalPoint() as [number, number, number];
    const eyeFocalDist = vec3.distance(curFocal, curPos);
    const position = newViewPlaneNormal.map(
      (v: number, i: number) => -v * eyeFocalDist + newfocalPoint![i],
    ) as [number, number, number];
    camera.setPosition(...position);
    camera.setViewUp(-viewUp[0], viewUp[1], viewUp[2]);
  }
  renderer.updateLightsGeometryToFollowCamera();
  renderer.resetCameraClippingRange();
  context.current.focus = focalPoint;
  requestAnimationFrame(() =>
    scheduleRender(rendererId!, { type: 'updateRenderWindow' }),
  );
}


export function updateAmbientLight(
  context: React.MutableRefObject< VRContext | null>,
  lightingOption: LightingOption,
){
  if(!context.current) return;
  const { viewportId, actorIds } = context.current;
  if(viewportId === 'VR'){
    Object.values(volumeNames).forEach((volumeKey) => {
      const volume = actorIds[volumeKey] as vtkVolume;
      if(!volume) return;
      const volumeProperty = volume.getProperty();
      if(volumeKey === volumeNames.MIP){
        volumeProperty.setShade(false);
        volumeProperty.modified();
        volume.modified();
        return;
      }
      setVolumeLighting(volumeProperty, lightingOption)
      volume.modified();
    });
  }
}


